import { Reference } from "@apollo/client";
import { Connection, Edge } from "@/lib/types/cursor-pagination";

export type ConnectionData = Connection<Reference> | undefined;

export function mergeConnections(
  existing: ConnectionData,
  incoming: ConnectionData,
  mapFn?: (data: Edge<Reference>[]) => Edge<Reference>[]
): ConnectionData {
  if (!existing || existing.edges.length === 0) {
    return incoming;
  }

  if (!incoming) {
    return existing;
  }

  // Create a Map for O(1) lookup of existing items by ID
  const existingMap = new Map<string, Edge<Reference>>();
  existing.edges.forEach((edge) => {
    existingMap.set(edge.node.__ref, edge);
  });

  // Filter out duplicates from incoming data
  const newEdges = incoming.edges.filter((edge) => !existingMap.has(edge.node.__ref));

  // Merge existing and new edges
  let mergedEdges = [...existing.edges, ...newEdges];
  if (mapFn) {
    mergedEdges = mapFn(mergedEdges);
  }

  // Update pageInfo - use incoming pageInfo as it represents the latest state
  const mergedPageInfo = {
    ...existing.pageInfo, // Keep all existing pageInfo fields including __typename
    hasNextPage: incoming.pageInfo.hasNextPage,
    hasPreviousPage: existing.pageInfo.hasPreviousPage, // Keep existing as we're appending
    startCursor: existing.pageInfo.startCursor, // Keep original start
    endCursor: incoming.pageInfo.endCursor, // Use latest end
  };

  return {
    ...existing, // Keep all existing fields including __typename
    edges: mergedEdges,
    pageInfo: mergedPageInfo,
  };
}
